'use client';

import { useCallback } from 'react';

import type { IUserStorageItem } from '@/api/userStorage';
import { useDeleteUserStorageItems } from './useDeleteUserStorageItems';
import { useSaveUserStorageItems } from './useSaveUserStorageItems';
import { useUserStorageItems } from './useUserStorageItems';

const upsert = (
  items: IUserStorageItem[],
  next: IUserStorageItem
): IUserStorageItem[] => {
  const index = items.findIndex((item) => item.identifier === next.identifier);

  if (index === -1) return [...items, next];

  const updated = [...items];
  updated[index] = next;
  return updated;
};

export const useUserStorageItem = <TInfo = Record<string, unknown>>(
  identifier: string
) => {
  const { items, isLoading, isReady, isAvailable, mutate } =
    useUserStorageItems();
  const { trigger: save } = useSaveUserStorageItems();
  const { trigger: remove } = useDeleteUserStorageItems();

  const info = items.find((item) => item.identifier === identifier)?.info as
    | TInfo
    | undefined;

  const setItem = useCallback(
    async (nextInfo: TInfo) => {
      const next: IUserStorageItem = {
        identifier,
        info: nextInfo as Record<string, unknown>,
      };

      mutate((current) => upsert(current ?? [], next), { revalidate: false });

      try {
        await save([next]);
      } catch {
        // Roll back the optimistic update by revalidating from the server.
        mutate();
      }
    },
    [identifier, mutate, save]
  );

  const removeItem = useCallback(async () => {
    mutate(
      (current) =>
        (current ?? []).filter((item) => item.identifier !== identifier),
      { revalidate: false }
    );

    try {
      await remove([identifier]);
    } catch {
      mutate();
    }
  }, [identifier, mutate, remove]);

  return { info, isLoading, isReady, isAvailable, setItem, removeItem };
};
